import { motion } from 'framer-motion'
import { Sparkles, Wand2, Images, ChevronDown, Mic, FileText, Video } from 'lucide-react'

interface HeroProps {
  onNavigate: (section: string) => void
}

export function Hero({ onNavigate }: HeroProps) {
  const tools = [
    { icon: Wand2, label: 'Image Gen', color: 'from-primary to-primary/60' },
    { icon: Video, label: 'Video Gen', color: 'from-secondary to-secondary/60' },
    { icon: Mic, label: 'Text to Speech', color: 'from-accent to-accent/60' },
    { icon: FileText, label: 'Script Writer', color: 'from-green-500 to-green-500/60' },
  ]

  return (
    <section className="min-h-screen flex flex-col items-center justify-center text-center px-6 pt-20 pb-16 relative">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-8"
      >
        <Sparkles className="w-4 h-4 animate-pulse" />
        All-in-One AI Creative Studio — Free Image, Video, Voice & Script Generation
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="font-display text-5xl md:text-7xl font-bold leading-tight tracking-tight mb-6"
      >
        Your Complete
        <br />
        <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-shift">
          AI Creative Studio
        </span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-10 leading-relaxed"
      >
        Generate stunning images, create videos, convert text to speech in 20+ languages,
        and write professional scripts — all in one powerful platform.
      </motion.p>

      {/* Tool Badges */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.25 }}
        className="flex flex-wrap justify-center gap-3 mb-10"
      >
        {tools.map((tool, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${tool.color} text-white text-sm font-medium shadow-lg`}
          >
            <tool.icon className="w-4 h-4" />
            {tool.label}
          </div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="flex flex-col sm:flex-row gap-4"
      >
        <button
          onClick={() => onNavigate('studio')}
          className="px-8 py-4 rounded-xl bg-gradient-to-r from-primary to-primary/80 text-white font-semibold shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-3"
        >
          <Wand2 className="w-5 h-5" />
          Open Studio
        </button>
        <button
          onClick={() => onNavigate('gallery')}
          className="px-8 py-4 rounded-xl bg-muted border border-border text-foreground font-semibold hover:bg-muted/80 transition-all flex items-center justify-center gap-3"
        >
          <Images className="w-5 h-5" />
          View Gallery
        </button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-muted-foreground text-xs"
      >
        <span>Scroll to explore</span>
        <ChevronDown className="w-4 h-4 animate-bounce" />
      </motion.div>
    </section>
  )
}
